import { ThingDataSubscriptionInfo } from '@eagle/core-data-types';
import { Alert, AlertTitle, Button, styled } from '@ethings/ui-toolkit';
import { DateTime } from 'luxon';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Portals } from '../../types';
import { switchToPortal } from '../../util/portal-switching';
import { getContractStatus } from './contract-management.utils';

const getRenewHref = (thingId?: string): string => {
  const path = thingId ? `contract-management?searchText=${encodeURIComponent(thingId)}` : 'contract-management';
  return switchToPortal(null, Portals.ADMIN, path) || '';
};

const STATUS_CONFIG = {
  'upcoming': { dateField: 'expiry' as const, severity: 'warning', dateLabelKey: 'common:contract-management.expiry.upcoming-date.label', descriptionKey: 'common:contract-management.expiry.upcoming.label' },
  'recently-expired': { dateField: 'expiry' as const, severity: 'error', dateLabelKey: 'common:contract-management.expiry.expired-date.label', descriptionKey: 'common:contract-management.expiry.expired.label' },
  'out-of-contract': { dateField: 'finish' as const, severity: 'error', dateLabelKey: 'common:contract-management.expiry.expired-date.label', descriptionKey: 'common:contract-management.expiry.expired.label' },
} as const;

const StyledAlert = styled(Alert)(({ theme }) => ({
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexWrap: 'wrap',
    '& .MuiAlert-icon': {
      display: 'none',
    },
    '& .MuiAlert-action': {
      padding: 0,
      margin: 0,
      width: '100%',
    },
  },
}));
const StyledButton = styled(Button)({ color: 'inherit !important' });

interface ContractStatusAlertProps {
  dataSubscription?: ThingDataSubscriptionInfo | null;
  sx?: Record<string, unknown>;
  thingId?: string;
  variant?: 'popup';
}

export const ContractStatusAlert: FC<ContractStatusAlertProps> = ({ dataSubscription, sx, thingId, variant }) => {
  const { t } = useTranslation(['common']);
  const contractStatus = getContractStatus(dataSubscription ?? undefined);

  if (contractStatus !== 'upcoming' && contractStatus !== 'recently-expired' && contractStatus !== 'out-of-contract') return null;

  const { dateField, severity, dateLabelKey, descriptionKey } = STATUS_CONFIG[contractStatus];
  const dateValue = dataSubscription?.lastDataSubscription?.[dateField];
  if (!dateValue) return null;

  const formattedDate = DateTime.fromJSDate(dateValue).toFormat('d LLLL yyyy');
  const dateLabel = t(dateLabelKey, { date: formattedDate });

  const buttonProps = {
    component: 'a' as const,
    href: getRenewHref(thingId),
    size: 'small' as const,
    color: severity,
    'data-testid': 'contract-status-renew-button',
  };

  if (variant === 'popup') {
    return (
      <StyledAlert
        data-testid="contract-status-alert"
        severity={severity}
        icon={false}
        sx={{ ...sx }}
        action={
          <StyledButton {...buttonProps}>
            {t('common:contract-management.renew.action')}
          </StyledButton>
        }
      >
        {dateLabel}
      </StyledAlert>
    );
  }

  return (
    <StyledAlert
      data-testid="contract-status-alert"
      severity={severity}
      sx={{ ...sx }}
      action={
        <StyledButton {...buttonProps}>
          {t('common:contract-management.renew-subscription.action')}
        </StyledButton>
      }
    >
      <AlertTitle>{dateLabel}</AlertTitle>
      {t(descriptionKey)}
    </StyledAlert>
  );
};
